export const getCountryName = (countryCode: string) => {
  const { $i18n } = useNuxtApp();

  if (!countryCode) {
    return $i18n.t("unknown");
  }

  if (countryCode === "GLOBAL") {
    return $i18n.t("global");
  }

  return $i18n.t(`countries.${countryCode.toUpperCase()}`);
};

export const getCountrySelectOptions = (locale: string = "da") => {
  const { $i18n } = useNuxtApp();

  const countries = ($i18n.messages.value as Record<string, Record<string, Record<string, string>>>)[locale].countries;
  return formatCountriesList(countries);
};

export const formatCountriesList = (countries: { [key: string]: string }): DropdownOption[] => {
  if (!countries) {
    return [];
  }

  const { $i18n } = useNuxtApp();

  let formattedCountries = Object.keys(countries).map((key) => ({
    label: $i18n.t(`countries.${key}`),
    value: key,
    flag: key.toLowerCase(),
  }));
  //sort the list with the da on top anf the scandic countries next
  formattedCountries = formattedCountries.sort(sortCountries);

  return formattedCountries;
};

const sortCountries = (a, b) => {
  if (a.value === "DK") return -1;
  if (b.value === "DK") return 1;
  if (a.value === "NO") return -1;
  if (b.value === "NO") return 1;
  if (a.value === "SE") return -1;
  if (b.value === "SE") return 1;
  if (a.value === "FI") return -1;
  if (b.value === "FI") return 1;

  return 0;
};

export const getDefaultCurrencyForCountry = (countryCode: string): string => {
  const countryToCurrency: Record<string, string> = {
    // Eurozone countries (19 core + territories)
    AD: "EUR",
    AT: "EUR",
    BE: "EUR",
    CY: "EUR",
    DE: "EUR",
    EE: "EUR",
    ES: "EUR",
    FI: "EUR",
    FR: "EUR",
    GF: "EUR",
    GP: "EUR",
    GR: "EUR",
    IE: "EUR",
    IT: "EUR",
    LT: "EUR",
    LU: "EUR",
    LV: "EUR",
    MC: "EUR",
    ME: "EUR",
    MF: "EUR",
    MT: "EUR",
    NL: "EUR",
    PM: "EUR",
    PT: "EUR",
    RE: "EUR",
    SI: "EUR",
    SK: "EUR",
    SM: "EUR",
    TF: "EUR",
    VA: "EUR",
    YT: "EUR",
    BL: "EUR",

    // Nordic countries
    DK: "DKK",
    FO: "DKK",
    GL: "DKK",
    NO: "NOK",
    SJ: "NOK",
    SE: "SEK",
    IS: "ISK",

    // USD Zone (US + territories)
    AS: "USD",
    GU: "USD",
    MP: "USD",
    PR: "USD",
    UM: "USD",
    US: "USD",
    VI: "USD",
    VG: "USD",
    EC: "USD",
    SV: "USD",
    PA: "USD",

    // GBP Zone (UK + Crown dependencies)
    GB: "GBP",
    IM: "GBP",
    JE: "GBP",
    GG: "GBP",
    FK: "GBP",
    GI: "GBP",
    GS: "GBP",
    IO: "GBP",
    SH: "GBP",
    TA: "GBP",

    // CHF Zone
    CH: "CHF",
    LI: "CHF",

    // Major economies
    AU: "AUD",
    CC: "AUD",
    CX: "AUD",
    HM: "AUD",
    KI: "AUD",
    NF: "AUD",
    NR: "AUD",
    TV: "AUD",
    BR: "BRL",
    CA: "CAD",
    CN: "CNY",
    HK: "HKD",
    MO: "MOP",
    IN: "INR",
    BT: "INR",
    JP: "JPY",
    KR: "KRW",
    MX: "MXN",
    NZ: "NZD",
    CK: "NZD",
    NU: "NZD",
    PN: "NZD",
    TK: "NZD",
    RU: "RUB",
    SG: "SGD",
    ZA: "ZAR",
    LS: "ZAR",
    NA: "ZAR",
    SZ: "ZAR",

    // African currencies
    DZ: "DZD",
    EG: "EGP",
    LY: "LYD",
    MA: "MAD",
    SD: "SDG",
    TN: "TND",
    BF: "XOF",
    BJ: "XOF",
    CI: "XOF",
    GW: "XOF",
    ML: "XOF",
    NE: "XOF",
    SN: "XOF",
    TG: "XOF",
    CF: "XAF",
    CG: "XAF",
    CM: "XAF",
    GA: "XAF",
    GQ: "XAF",
    TD: "XAF",
    BI: "BIF",
    DJ: "DJF",
    ER: "ERN",
    ET: "ETB",
    GH: "GHS",
    GN: "GNF",
    KE: "KES",
    KM: "KMF",
    MG: "MGA",
    MZ: "MZN",
    NG: "NGN",
    RW: "RWF",
    SO: "SOS",
    TZ: "TZS",
    UG: "UGX",
    ZM: "ZMK",
    ZW: "ZWL",
    // Countries using other currencies
    AO: "USD",
    LR: "USD",
    MW: "USD",
    SC: "USD",
    SL: "USD",
    SS: "USD",

    // Middle East currencies
    AE: "AED",
    BH: "BHD",
    IL: "ILS",
    IQ: "IQD",
    IR: "IRR",
    JO: "JOD",
    KW: "KWD",
    LB: "LBP",
    OM: "OMR",
    PS: "ILS",
    QA: "QAR",
    SA: "SAR",
    SY: "SYP",
    YE: "YER",

    // Asian currencies
    AF: "AFN",
    AM: "AMD",
    AZ: "AZN",
    BD: "BDT",
    BN: "BND",
    KH: "KHR",
    ID: "IDR",
    KZ: "KZT",
    MY: "MYR",
    MM: "MMK",
    NP: "NPR",
    PK: "PKR",
    PH: "PHP",
    LK: "LKR",
    TH: "THB",
    UZ: "UZS",
    VN: "VND",
    // Countries using other currencies for stability
    KG: "USD",
    LA: "USD",
    MV: "USD",
    MN: "USD",
    TJ: "USD",
    TM: "USD",

    // Eastern European currencies
    AL: "ALL",
    BA: "BAM",
    BG: "BGN",
    BY: "BYN",
    CZ: "CZK",
    GE: "GEL",
    HR: "HRK",
    HU: "HUF",
    MD: "MDL",
    MK: "MKD",
    PL: "PLN",
    RO: "RON",
    RS: "RSD",
    TR: "TRY",
    UA: "UAH",

    // Americas currencies
    AR: "ARS",
    BZ: "BZD",
    BO: "BOB",
    CL: "CLP",
    CO: "COP",
    CR: "CRC",
    DO: "DOP",
    GT: "GTQ",
    HN: "HNL",
    JM: "JMD",
    NI: "NIO",
    PY: "PYG",
    PE: "PEN",
    TT: "TTD",
    UY: "UYU",
    VE: "VEF",
    // Countries using USD or other stable currencies
    BB: "USD",
    CU: "USD",
    GY: "USD",
    HT: "USD",
    SR: "USD",

    // Pacific currencies
    TO: "TOP",
    // Pacific islands using USD/AUD/NZD for stability
    FJ: "USD",
    PG: "USD",
    SB: "USD",
    VU: "USD",
    WS: "USD",

    // Special cases and remaining territories
    AQ: "USD",
    BV: "NOK",
    TW: "TWD",

    // Small territories and Caribbean
    AI: "USD",
    AG: "USD",
    DM: "USD",
    GD: "USD",
    KN: "USD",
    LC: "USD",
    MS: "USD",
    VC: "USD",
    AW: "USD",
    BM: "USD",
    BS: "USD",
    KY: "USD",
    TC: "USD",
    CV: "CVE",
    ST: "USD",
    PW: "USD",
    FM: "USD",
    MH: "USD",
  };
  return countryToCurrency[countryCode] || "EUR";
};
